import React, { useState, useEffect, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { LogOut, Trash2, Instagram, Building2, User, Calendar, ArrowLeft, Phone } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

export default function AdminPanel() {
  const [token, setToken] = useState(localStorage.getItem('admin_token') || '');
  const [user, setUser] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const authHeader = useCallback(() => ({
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    axios.get(`${API}/auth/me`, authHeader())
      .then(res => setUser(res.data))
      .catch(() => { setToken(''); localStorage.removeItem('admin_token'); })
      .finally(() => setLoading(false));
  }, [token, authHeader]);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/leads`, authHeader())
      .then(res => setLeads(res.data.leads || []))
      .catch(err => console.error('Failed to fetch leads:', err));
  }, [user, authHeader]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoggingIn(true);
    try {
      const res = await axios.post(`${API}/auth/login`, loginForm);
      const t = res.data.token;
      localStorage.setItem('admin_token', t);
      setToken(t);
      setUser(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setLoginError(typeof detail === 'string' ? detail : 'Invalid credentials');
    }
    setLoggingIn(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setToken('');
    setUser(null);
    setLeads([]);
  };

  const handleDelete = async (leadId) => {
    try {
      await axios.delete(`${API}/leads/${leadId}`, authHeader());
      setLeads(prev => prev.filter(l => l.id !== leadId));
    } catch (err) {
      console.error('Failed to delete lead:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-6 h-6 border-2 border-slate-300 border-t-slate-900 rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6" data-testid="admin-login-page">
        <div className="w-full max-w-sm">
          <a href="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 mb-8 transition-colors">
            <ArrowLeft size={14} /> Back to website
          </a>
          <div className="bg-white border border-slate-200 rounded-2xl p-8">
            <h1 className="text-2xl font-medium text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="admin-login-title">
              Admin Login
            </h1>
            <p className="text-sm text-slate-500 mb-6">Sign in to view your leads</p>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="admin-email" className="text-sm text-slate-700 mb-1.5 block">Email</Label>
                <Input id="admin-email" data-testid="admin-email-input" type="email" value={loginForm.email} onChange={e => setLoginForm(p => ({ ...p, email: e.target.value }))} placeholder="admin@bigjaguar.in" className="h-11" />
              </div>
              <div>
                <Label htmlFor="admin-password" className="text-sm text-slate-700 mb-1.5 block">Password</Label>
                <Input id="admin-password" data-testid="admin-password-input" type={showPassword ? "text" : "password"} value={loginForm.password} onChange={e => setLoginForm(p => ({ ...p, password: e.target.value }))} placeholder="Enter password" className="h-11" />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="show-password"
                  data-testid="show-password-checkbox"
                  checked={showPassword}
                  onChange={e => setShowPassword(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900 cursor-pointer"
                />
                <label htmlFor="show-password" className="text-sm text-slate-500 cursor-pointer select-none">Show password</label>
              </div>
              {loginError && <p className="text-sm text-red-500" data-testid="admin-login-error">{loginError}</p>}
              <Button type="submit" data-testid="admin-login-button" className="w-full h-11 bg-slate-900 text-white hover:bg-slate-800 rounded-xl" disabled={loggingIn}>
                {loggingIn ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Leads are already sorted by date desc from backend. Serial = index + 1 (newest first)
  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" className="text-sm text-slate-500 hover:text-slate-700 transition-colors"><ArrowLeft size={16} /></a>
            <h1 className="text-lg font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="admin-title">
              Leads Dashboard
            </h1>
            <span className="bg-slate-100 text-slate-600 text-xs font-medium px-2.5 py-1 rounded-full" data-testid="leads-count">
              {leads.length} lead{leads.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-500">{user.email}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="admin-logout-button" className="text-slate-500 hover:text-slate-700">
              <LogOut size={16} />
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {leads.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center" data-testid="no-leads-message">
            <p className="text-slate-400 text-sm">No leads yet. They'll appear here as visitors submit the form.</p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            {/* Table header */}
            <div className="grid grid-cols-[60px_1fr_1fr_1fr_1fr_160px_48px] gap-4 px-5 py-3 bg-slate-50 border-b border-slate-200 text-xs font-medium text-slate-500 uppercase tracking-wider hidden md:grid">
              <span>#</span>
              <span>Name</span>
              <span>Business</span>
              <span>Phone</span>
              <span>Instagram</span>
              <span>Date</span>
              <span></span>
            </div>

            {leads.map((lead, index) => (
              <div
                key={lead.id}
                className="grid grid-cols-1 md:grid-cols-[60px_1fr_1fr_1fr_1fr_160px_48px] gap-2 md:gap-4 px-5 py-4 border-b border-slate-100 last:border-b-0 hover:bg-slate-50/50 transition-colors group items-center"
                data-testid={`lead-row-${lead.id}`}
              >
                {/* Serial number */}
                <span className="text-sm font-medium text-slate-400 hidden md:block" data-testid={`lead-serial-${lead.id}`}>
                  {index + 1}
                </span>

                {/* Mobile: serial + name */}
                <div className="flex items-center gap-2 md:contents">
                  <span className="text-xs font-medium text-slate-400 bg-slate-100 rounded-full w-6 h-6 flex items-center justify-center md:hidden">
                    {index + 1}
                  </span>
                  <div className="flex items-center gap-2 min-w-0">
                    <User size={14} className="text-slate-400 flex-shrink-0 hidden md:block" />
                    <span className="text-sm font-medium text-slate-900 truncate" data-testid={`lead-name-${lead.id}`}>{lead.name}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2 min-w-0 pl-8 md:pl-0">
                  <Building2 size={14} className="text-slate-400 flex-shrink-0 hidden md:block" />
                  <span className="text-sm text-slate-600 truncate" data-testid={`lead-business-${lead.id}`}>{lead.business}</span>
                </div>

                <div className="flex items-center gap-2 min-w-0 pl-8 md:pl-0">
                  <Phone size={14} className="text-slate-400 flex-shrink-0 hidden md:block" />
                  <span className="text-sm text-slate-500 truncate" data-testid={`lead-phone-${lead.id}`}>{lead.phone || '-'}</span>
                </div>

                <div className="flex items-center gap-2 min-w-0 pl-8 md:pl-0">
                  <Instagram size={14} className="text-slate-400 flex-shrink-0 hidden md:block" />
                  <span className="text-sm text-slate-500 truncate" data-testid={`lead-instagram-${lead.id}`}>{lead.instagram || '-'}</span>
                </div>

                <div className="flex items-center gap-2 min-w-0 pl-8 md:pl-0">
                  <Calendar size={14} className="text-slate-300 flex-shrink-0 hidden md:block" />
                  <span className="text-xs text-slate-400">
                    {new Date(lead.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>

                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(lead.id)}
                  data-testid={`delete-lead-${lead.id}`}
                  className="text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all ml-auto md:ml-0"
                >
                  <Trash2 size={15} />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
